(() => {
  // A literal zero appended to {{amount}} is display padding, not currency text.
  // BUCKS otherwise strips the first zero from amounts such as 30.000 -> 3.000.
  function normalizeBucksBaseFormat() {
    const config = window.bucksCC?.metaConfig;
    if (config?.baseCurrency !== 'KWD') return;
    let changed = false;
    ['baseMoneyFormat', 'baseMoneyWithCurrencyFormat'].forEach((key) => {
      const original = config[key];
      if (typeof original !== 'string') return;
      const normalized = original.replace(/(\{\{\s*amount\s*\}\})0(?=\s|<|$)/g, '$1');
      if (normalized !== original) {
        config[key] = normalized;
        changed = true;
      }
    });
    if (!changed) return;
    document.querySelectorAll('span.money[bucks-original]').forEach((money) => {
      const original = money.getAttribute('bucks-original');
      const replacement = document.createElement('span');
      replacement.className = 'money';
      replacement.textContent = original;
      money.replaceWith(replacement);
    });
  }
  document.addEventListener('BUCKS_SELECTED_CURRENCY', (event) => {
    if (typeof event.detail === 'string') window.thaatDisplayedCurrency = event.detail;
  });
  // Give BUCKS fresh base-currency markup after Shopify replaces a price.
  let conversionFrame;
  window.thaatRefreshCurrency = () => {
    cancelAnimationFrame(conversionFrame);
    conversionFrame = requestAnimationFrame(() => {
      normalizeBucksBaseFormat();
      window.bucksCC?.reConvert?.();
    });
  };
  document.addEventListener('product-info:loaded', window.thaatRefreshCurrency);
  window.addEventListener('BUCKSCC-LOADED', window.thaatRefreshCurrency);

  function formatMoney(cents, format) {
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ''],
      amount_with_period_and_space_separator: [2, ' ', '.']
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      const [precision, thousands, decimal] = formats[token] || formats.amount;
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    });
  }

  if (!customElements.get('thaat-product-total')) {
    customElements.define('thaat-product-total', class extends HTMLElement {
      connectedCallback() {
        this.lifecycle?.abort();
        this.lifecycle = new AbortController();
        this.product = this.closest('product-info');
        const updateQuantity = (event) => {
          if (event.target.matches('input[name="quantity"]')) this.render();
        };
        this.product?.addEventListener('input', updateQuantity, { signal: this.lifecycle.signal });
        this.product?.addEventListener('change', updateQuantity, { signal: this.lifecycle.signal });
        this.render();
      }

      disconnectedCallback() {
        this.lifecycle?.abort();
      }

      setVariant(variant) {
        this.hidden = !variant;
        if (!variant) return;
        this.dataset.unitPrice = variant.price;
        this.dataset.comparePrice = variant.compare_at_price || 0;
        this.render();
      }

      render() {
        const quantityInput = this.product?.querySelector('input[name="quantity"]');
        const quantity = Number(quantityInput?.value || quantityInput?.min || 1);
        const unit = Number(this.dataset.unitPrice);
        const compare = Number(this.dataset.comparePrice || 0);
        if (!Number.isFinite(quantity) || quantity < 1 || !Number.isFinite(unit)) return;
        const price = this.querySelector('[data-total-price]');
        const oldPrice = this.querySelector('[data-total-compare]');
        const format = this.dataset.moneyFormat;
        if (!price || !oldPrice || !format) return;
        const key = [unit, compare, quantity, format].join('|');
        if (this.lastRender === key) return;
        this.lastRender = key;
        // Replace the wrapper as well as its text so converter metadata cannot go stale.
        price.innerHTML = formatMoney(Math.round(unit * quantity), format);
        oldPrice.hidden = compare <= unit;
        oldPrice.innerHTML = compare > unit ? formatMoney(Math.round(compare * quantity), format) : '';
        window.thaatRefreshCurrency();
      }
    });
  }

  if (!customElements.get('thaat-currency-selector')) {
    customElements.define('thaat-currency-selector', class extends HTMLElement {
      connectedCallback() {
        this.lifecycle?.abort();
        this.lifecycle = new AbortController();
        const { signal } = this.lifecycle;
        this.select = this.querySelector('select');
        this.select.addEventListener('change', () => {
          const code = this.select.value;
          // Use the app's own switcher so its rates, preference and Markets handling stay authoritative.
          const trigger = Array.from(document.querySelectorAll('.buckscc-select-options .converterTriggers'))
            .find((item) => item.getAttribute('rel') === code && !item.classList.contains('geoCurrency'));
          if (trigger) {
            normalizeBucksBaseFormat();
            trigger.click();
          }
        }, { signal });
        const sync = () => this.mount();
        window.addEventListener('BUCKSCC-LOADED', sync, { signal });
        document.addEventListener('BUCKS_SELECTED_CURRENCY', sync, { signal });
        document.addEventListener('shopify:section:load', sync, { signal });
        this.observer = new MutationObserver((records) => {
          if (records.some((record) => Array.from(record.addedNodes).some((node) =>
            node.nodeType === 1 && (node.matches?.('.buckscc-currency-wrapper, #bucksSelector') ||
              node.querySelector?.('#bucksSelector'))))) this.mount();
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
        this.mount();
      }

      disconnectedCallback() {
        this.lifecycle?.abort();
        this.observer?.disconnect();
      }

      mount() {
        const source = document.querySelector('#bucksSelector');
        if (!source?.options.length || !window.bucksCC?.metaConfig?.active) return;
        const names = { KWD: 'دينار كويتي', SAR: 'ريال سعودي', AED: 'درهم إماراتي',
          QAR: 'ريال قطري', BHD: 'دينار بحريني', OMR: 'ريال عماني' };
        const arabic = document.documentElement.lang.startsWith('ar');
        const signature = Array.from(source.options).map((option) => option.value).join(',');
        if (this.signature !== signature) {
          this.signature = signature;
          this.select.replaceChildren(...Array.from(source.options).map((option) =>
            new Option(arabic && names[option.value] ? names[option.value] + ' · ' + option.value : option.text, option.value)));
        }
        this.select.value = window.thaatDisplayedCurrency ||
          document.querySelector('.bucks-selected')?.textContent.trim() || source.value;
        this.hidden = false;
        const fallback = this.nextElementSibling;
        if (fallback?.hasAttribute('data-thaat-currency-fallback')) fallback.hidden = true;
      }
    });
  }
})();
